import clsx from "clsx";

import type { CoachAssignment, CoachPlanDay } from "@flexv-coach/shared";
import { EmptyState } from "@/components/ui/States";
import { formatDayMonth, formatWeekday, isToday } from "@/lib/date";
import { useI18n } from "@/hooks/useI18n";
import { AssignmentCard } from "./AssignmentCard";

interface WeekGridProps {
    days: CoachPlanDay[];
    onSelect: (assignment: CoachAssignment) => void;
}

/**
 * Papan kalender 7 hari (desktop).
 *
 * Kolom hari ini diberi aksen supaya mata langsung nemu posisi "sekarang";
 * hari kosong tetap ditampilkan (bukan disembunyikan) karena informasi
 * "hari ini libur" sama pentingnya dengan "hari ini ada sesi".
 */
export function WeekGrid({ days, onSelect }: WeekGridProps) {
    const { t } = useI18n();
    const hasAny = days.some((day) => day.assignments.length > 0);

    if (!hasAny) return null;

    return (
        <div className="hidden lg:grid lg:grid-cols-7 lg:gap-3">
            {days.map((day) => {
                const today = isToday(day.date);
                const count = day.assignments.length;

                return (
                    <div key={day.date} className="flex min-w-0 flex-col gap-2">
                        <div
                            className={clsx(
                                "rounded-xl border px-3 py-2 text-center transition-colors",
                                today
                                    ? "app-accent-soft border-[var(--color-primary)]/40"
                                    : "app-content-subtle app-shell-border",
                            )}
                        >
                            <p
                                className={clsx(
                                    "text-xs font-bold uppercase tracking-wide",
                                    today ? "app-accent-text" : "app-shell-text-muted",
                                )}
                            >
                                {formatWeekday(day.date)}
                            </p>
                            <p className="app-text-primary text-sm font-semibold">
                                {formatDayMonth(day.date)}
                            </p>
                            {count > 0 && (
                                <span
                                    className={clsx(
                                        "mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold",
                                        today
                                            ? "bg-[var(--color-primary)] text-white"
                                            : "app-accent-soft app-accent-text",
                                    )}
                                >
                                    {t("plan.day.sessionCount", { count })}
                                </span>
                            )}
                        </div>

                        <div className="flex flex-col gap-2">
                            {count === 0 ? (
                                <div className="app-content-subtle rounded-xl border border-dashed app-shell-border py-8 text-center">
                                    <p className="app-text-soft text-xs">{t("plan.day.off")}</p>
                                </div>
                            ) : (
                                day.assignments.map((assignment) => (
                                    <AssignmentCard
                                        key={assignment.id}
                                        assignment={assignment}
                                        onSelect={onSelect}
                                        compact
                                    />
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

interface DayListProps {
    days: CoachPlanDay[];
    onSelect: (assignment: CoachAssignment) => void;
}

/** Versi mobile: daftar per hari (grid 7 kolom tidak muat). */
export function DayList({ days, onSelect }: DayListProps) {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-4 lg:hidden">
            {days.map((day) => (
                <div key={day.date}>
                    <div className="mb-2 flex items-center gap-2">
                        <p
                            className={clsx(
                                "app-text-primary text-sm font-bold",
                                isToday(day.date) && "app-accent-text",
                            )}
                        >
                            {formatWeekday(day.date)}, {formatDayMonth(day.date)}
                        </p>
                        {isToday(day.date) && (
                            <span className="app-accent-soft app-accent-text rounded-full px-2 py-0.5 text-[11px] font-semibold">
                                {t("plan.today")}
                            </span>
                        )}
                        <div className="app-shell-border flex-1 border-t border-dashed" />
                    </div>

                    {day.assignments.length === 0 ? (
                        <p className="app-text-soft py-2 text-sm">{t("plan.day.empty")}</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {day.assignments.map((assignment) => (
                                <AssignmentCard
                                    key={assignment.id}
                                    assignment={assignment}
                                    onSelect={onSelect}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export function PlanEmpty({ message }: { message: string }) {
    return <EmptyState title="No sessions" description={message} />;
}
